import { FancyShareDto } from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { SectionDesign } from '@diritto/common/home/constants/section-constants';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { SectionRelayDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps } from '@diritto/common/tracker/tracking-event.constant';
import { SectionBarBanner } from 'app-components/home/sections/SectionBarBanner';
import { SectionContentBanner } from 'app-components/home/sections/SectionContentBanner';
import { SectionContentCompacts } from 'app-components/home/sections/SectionContentCompacts';
import SectionContentLites from 'app-components/home/sections/SectionContentLites';
import SectionContents from 'app-components/home/sections/SectionContents';
import { SectionCustomQuickMenu } from 'app-components/home/sections/SectionCustomQuickMenu';
import SectionFancyShares from 'app-components/home/sections/SectionFancyShares';
import { SectionFavoriteCategories } from 'app-components/home/sections/SectionFavoriteCategories';
import { SectionQuickMenu } from 'app-components/home/sections/SectionQuickMenu';
import SectionRankLites from 'app-components/home/sections/SectionRankLites';
import SectionRanks from 'app-components/home/sections/SectionRanks';
import SectionRecentlyReadContents from 'app-components/home/sections/SectionRecentlyReadContents';
import { SectionRectBanner } from 'app-components/home/sections/SectionRectBanner';
import SectionReplies from 'app-components/home/sections/SectionReplies';
import SectionRollings from 'app-components/home/sections/SectionRollings';
import { SectionSearchKeywords } from 'app-components/home/sections/SectionSearchKeywords';
import { SectionEventContent } from 'app-components/home/sections/SectionEventContent';
import { useAuthState } from 'contexts/AuthProvider';
import { SentryService } from 'utils/SentryService';
import SectionExclusiveContents from 'app-components/home/sections/SectionExclusiveContents';

type SectionProps = {
  section: SectionRelayDto;
  contents: Record<number, ContentRelayDto>;
  episodes: Record<number, EpisodeRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
  replies: Record<number, ReplyRelayDto>;
  fancyShares: Record<number, FancyShareDto>;
  rollings: Record<number, RollingRelayDto>;
};

export const SectionDefault = ({
  section,
  contents,
  episodes,
  profileLites,
  replies,
  fancyShares,
  rollings,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { partner } = useAuthState();

  switch (section.design) {
    case SectionDesign.Content:
      return (
        <SectionContents
          key={section.id}
          section={section}
          contents={contents}
          profileLites={profileLites}
          showDescription
          showCategory
          showKeyword
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.ContentLite:
      return (
        <SectionContentLites
          key={section.id}
          section={section}
          contents={contents}
          profileLites={profileLites}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.ContentCompact:
      return (
        <SectionContentCompacts
          key={section.id}
          section={section}
          contents={contents}
          profileLites={profileLites}
          showDescription
          showCategory
          showKeyword
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.RecentlyReadContent:
      return <SectionRecentlyReadContents key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.Rank:
      return (
        <SectionRanks
          key={section.id}
          section={section}
          contents={contents}
          profileLites={profileLites}
          showViewCount
          showCategory
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.RankLite:
      return (
        <SectionRankLites
          key={section.id}
          section={section}
          contents={contents}
          profileLites={profileLites}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.Reply:
      return (
        <SectionReplies
          key={section.id}
          section={section}
          contents={contents}
          episodes={episodes}
          profileLites={profileLites}
          replies={replies}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.FancyShare:
      return (
        <SectionFancyShares
          key={section.id}
          section={section}
          profileLites={profileLites}
          fancyShares={fancyShares}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.Rolling:
      return (
        <SectionRollings
          key={section.id}
          section={section}
          profileLites={profileLites}
          rollings={rollings}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.QuickMenu:
      return <SectionQuickMenu key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.CustomQuickMenu:
      if (partner) {
        return null;
      }
      return <SectionCustomQuickMenu key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.Banner:
      return <SectionBarBanner key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.BarBanner:
      return <SectionBarBanner key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.LargeRectBanner:
    case SectionDesign.SmallRectBanner:
      return <SectionRectBanner key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.ContentBanner:
      return (
        <SectionContentBanner
          key={section.id}
          section={section}
          contents={contents}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.SearchKeyword:
      return <SectionSearchKeywords key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.FavoriteCategory:
      return <SectionFavoriteCategories key={section.id} section={section} trackerExtraInfo={trackerExtraInfo} />;
    case SectionDesign.EventContent:
      return (
        <SectionEventContent
          key={section.id}
          section={section}
          contents={contents}
          profileLites={profileLites}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    case SectionDesign.ExclusiveContent:
      return (
        <SectionExclusiveContents
          key={section.id}
          section={section}
          contents={contents}
          profileLites={profileLites}
          trackerExtraInfo={trackerExtraInfo}
        />
      );
    default:
      SentryService.captureMessage(`Section design not found: ${JSON.stringify(section)}`);
      return null;
  }
};

export default SectionDefault;
